/**
 * The effects channel ([Sound](../../../docs/SOUND.md)).
 *
 * The second of the three audio channels, and the only one this app can
 * actually have on the web: **voice**, **effects** and **haptics**, each muted
 * independently. Haptics do not exist here — Apple has never implemented the
 * Vibration API in iOS Safari — so the tonal system carries the whole feedback
 * load, which it was designed to do anyway.
 *
 * Three rules from that document are enforced here rather than described:
 *
 * - **Voice is never ducked by an effect.** A cue that would land while
 *   Sneakers is speaking is *dropped*, not queued and not mixed under him. The
 *   narrator is the app; everything else is decoration, and decoration that
 *   talks over the teacher is worse than no decoration.
 * - **Muting effects never mutes voice**, and muting everything still leaves a
 *   fully playable app. Nothing here is load-bearing.
 * - **Fast or not at all.** The correct/not-yet cue must land within 50 ms of
 *   the tap or the feedback feels detached, so files are preloaded and a cue
 *   that is not ready yet is skipped rather than played late.
 *
 * The files are generated ([`tools/sound/`](../../../tools/sound/build.py)),
 * for the reason [D-080](../../../docs/DECISIONS.md#d-080) settled for the
 * wardrobe. They are seventeen short mallet hits; a real marimba and a good ear
 * would be better, and dropping the recordings in is the whole job.
 */

import { ASSET_PATH, whileSpeaking } from './audio.js';

/** Every cue the generator emits. A name not in here cannot be played. */
export const CUES = [
  'tap',
  'hint',
  'progress',
  'correct',
  'not-yet',
  'correct-hard',
  'combo-3',
  'combo-5',
  'lesson-complete',
  'streak',
  'recap',
  'chest-appears',
  'chest-opens',
  'rare',
  'buy',
  'equip',
  'bonus-start',
] as const;

export type Cue = (typeof CUES)[number];

const KEY = 'capytalk.effects';

/**
 * Whether effects are on.
 *
 * `localStorage` rather than the server: it is a property of *this device in
 * this room*, and a child who turns the sound off on the bus should not find it
 * off on the iPad at home. Defaults to on, and any failure to read it — private
 * browsing, blocked storage — also means on, because the default is the
 * designed experience.
 */
export function effectsOn(): boolean {
  try {
    return globalThis.localStorage?.getItem(KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setEffects(on: boolean): void {
  try {
    globalThis.localStorage?.setItem(KEY, on ? 'on' : 'off');
  } catch {
    // A device that will not remember the setting still honours it for this
    // session — `effectsOn` is only consulted at play time.
  }
}

/** Preloaded elements, one per cue. Empty until `warmUp` has run. */
const ready = new Map<Cue, HTMLAudioElement>();

/**
 * Whether Sneakers is talking.
 *
 * **Subscribed, not pushed.** The voice channel already publishes where it is
 * in a line — the mouth is drawn from it — so this listens to that rather than
 * asking `audio.ts` to call in. The dependency stays one-way, and there is no
 * call site anywhere that has to remember to say "a voice started": forgetting
 * one would mean an effect over the narrator, which is the rule this exists to
 * keep.
 */
let speaking = false;

/**
 * Load the set.
 *
 * Called once, after the first interaction — browsers refuse to construct
 * audio before a gesture, and a console full of autoplay warnings on launch is
 * how a real failure gets missed later.
 */
export function warmUp(): void {
  if (ready.size > 0) return;
  if (typeof Audio !== 'function') return;

  // The voice's own progress signal: a number while a line is being said,
  // null between lines.
  whileSpeaking((at) => {
    speaking = at !== null;
  });

  for (const cue of CUES) {
    try {
      const element = new Audio(`${ASSET_PATH}/audio/ui/${cue}.mp3`);
      element.preload = 'auto';
      ready.set(cue, element);
    } catch {
      // A device that will not construct one simply has no effects. The app
      // does not need them.
    }
  }
}

/**
 * Play a cue, or do nothing.
 *
 * Never throws and never awaits: a sound that will not play is a sound the
 * child does not hear, and nothing above this line should have to care.
 */
export function play(cue: Cue): void {
  if (!effectsOn()) return;
  // Voice is never ducked. Dropped rather than queued: a cue that arrives after
  // its moment is worse than one that never came.
  if (speaking) return;

  const element = ready.get(cue);
  if (element === undefined) return;

  try {
    // Rewound rather than cloned, so a fast row of taps cannot pile up a
    // hundred elements. The cost is that a cue cannot overlap itself, which for
    // sounds this short is what you want anyway.
    element.currentTime = 0;
    void element.play().catch(() => undefined);
  } catch {
    // Ignored on purpose. See above.
  }
}

/**
 * The correct sound, pitched by how long the run is
 * ([Sound](../../../docs/SOUND.md)).
 *
 * "Pitch rises with the combo — 5 in a row sounds different from 1." The cue
 * files are fixed, so the run picks *which* cue rather than transposing one:
 * three and five have their own sounds, and hard mode has its brighter timbre.
 */
export function correct(run: number, hardMode: boolean): void {
  if (run >= 5) return play('combo-5');
  if (run >= 3) return play('combo-3');
  return play(hardMode ? 'correct-hard' : 'correct');
}
